import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import cx from "classnames";

import store from "store";
import Socket from "utils/socket/public";
import { get_ticker24h } from "api/v4/market";
import {
  getCfdQuoteCurrencyUpper,
  isUsdtMarginQuoteCode,
  matchCfdSymbolMarginQuote,
} from "@/components/Pages/Market/MarketList/cfdTabListSource";
import { getDynamicFiatTabCodes, isFiatQuoteCurrency } from "@/utils/getFiatCurrencyMap";
import useTimeout from "@/hooks/useTimeout";
import XtLoading from "@/components/Xt/Loading";
import XtScrollWindow from "@/components/Xt/scroll/window";
import AppDivNoData from "@/components/App/div/noData";

import List from "./list";

import styles from "./index.module.scss";

import { TabCfgProps } from "../tabs";
import { TickerProps } from "store/trade";

interface Props {
  keyword: string;
  sortBy: string | undefined;
  tabCfg: TabCfgProps;
  isShowVolume: boolean;
  onMarketSelect?: () => void;
}

const WindowUl: React.FC<any> = ({ startIndex, record, isShowVolume, onMarketSelect }) => {
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (disabled) {
      setTimeout(() => {
        setDisabled(false);
      }, 500);
    }
  }, [disabled]);

  return record.map((item, index) => {
    return (
      <List
        key={`${item.s}-${startIndex + index}`}
        style={{ top: (startIndex + index) * 30 + "px" }}
        ticker={item}
        isShowVolume={isShowVolume}
        disabled={disabled}
        onClick={() => {
          setDisabled(true);
          onMarketSelect?.();
        }}
      />
    );
  });
};

function isDisplayLevelAllowed(marketConfig, allowedLevels?: string[]) {
  if (!marketConfig) return false;
  if (marketConfig.state === "DELISTED") return false;
  const displayLevel = String(marketConfig.displayLevel || "").toUpperCase();
  if (displayLevel === "NONE") return false;
  if (!allowedLevels || allowedLevels.length === 0) return true;
  return allowedLevels.includes(displayLevel);
}

const Main: React.FC<Props> = ({ keyword, sortBy, tabCfg, isShowVolume, onMarketSelect }) => {
  const router = useRouter();
  const { config } = store.market;
  const { tickers } = store.trade;
  const { isLogin, symbolStar } = store.user;

  const isCfd = useMemo(() => {
    const byRoute = router.pathname?.startsWith("/futures/") || router.asPath?.startsWith("/futures/");
    const byStore = !!store.market.name && store.market.name.toLowerCase().endsWith("_cfd");
    return byRoute || byStore;
  }, [router.pathname, router.asPath, store.market.name]);

  const isFiatContract = useMemo(() => {
    if (!isCfd) return false;
    const { name } = store.market;
    if (!name) return false;
    return isFiatQuoteCurrency(getCfdQuoteCurrencyUpper(name));
  }, [isCfd, store.market.name]);

  /** 法币本位：当前页交易对计价币（如 BRL），commodities/stocks/crypto/Top Search 仅展示该法币 */
  const activeFiatQuote = useMemo(() => {
    if (!isFiatContract) return null;
    const q = getCfdQuoteCurrencyUpper(store.market.name);
    return q ? q.toLowerCase() : null;
  }, [isFiatContract, store.market.name]);

  const [loading, setLoading] = useState<boolean>(true);

  const apiReqTicker24h = useCallback(
    (callback) => {
      if (!config) return;
      let resData: TickerProps[] = [];
      get_ticker24h()
        .then((data) => {
          resData = data;
        })
        .catch(() => {
          // empty
        })
        .finally(() => {
          const ary: TickerProps[] = [];
          let doc: TickerProps;
          for (const va in config) {
            doc = resData.find((obj) => obj.s === va) || { s: va };
            ary.push(doc);
          }
          store.trade.updateState({ tickers: ary });
          callback && callback(ary);
        });
    },
    [config]
  );

  const [apiReqTicker24hLoop] = useTimeout(apiReqTicker24h, { ms: 5000, isLoop: true, isInitExec: true });

  const wsNotReadyApiLoop = useCallback(() => {
    !Socket.isReady && apiReqTicker24hLoop();
  }, [apiReqTicker24hLoop]);
  const [timeoutApiLoop, timeoutClearApiLoop] = useTimeout(wsNotReadyApiLoop, { ms: 300 });

  const wsCallback = useCallback(
    (records) => {
      timeoutClearApiLoop();
      const newAry = [...store.trade.tickers];
      records.map((obj) => {
        const index = newAry.findIndex((doc) => doc.s === obj.s);
        if (index < 0) newAry.push(obj);
        else newAry.splice(index, 1, obj);
      });
      store.trade.updateState({ tickers: newAry });
    },
    [timeoutClearApiLoop]
  );

  const isSearch = useMemo(() => !!keyword, [keyword]);

  const doSortArray = useCallback(
    (records, nextSortBy) => {
      if (!config) return records;
      const isUserSort = !isSearch && tabCfg.key === "user";

      const newAry: TickerProps[] = (() => {
        if (!isUserSort) return [...records];
        const ary: TickerProps[] = [];
        symbolStar.map((symbol) => {
          const doc = records.find((obj) => obj.s === symbol);
          doc && ary.push(doc);
        });
        return ary;
      })();

      newAry.sort((x, y) => {
        if (nextSortBy) {
          const ary = nextSortBy.split("_");
          if (ary[0] === "name") {
            if (ary[1] === "up") return strCompare(y.s, x.s);
            if (ary[1] === "down") return strCompare(x.s, y.s);
          }
          if (ary[0] === "price") {
            if (ary[1] === "up") return +(x.c || 0) - +(y.c || 0);
            if (ary[1] === "down") return +(y.c || 0) - +(x.c || 0);
          }
          if (ary[0] === "rate") {
            if (ary[1] === "up") return +(x.cr || 0) - +(y.cr || 0);
            if (ary[1] === "down") return +(y.cr || 0) - +(x.cr || 0);
          }
          if (ary[0] === "volume") {
            if (ary[1] === "up") return +(x.q || 0) - +(y.q || 0);
            if (ary[1] === "down") return +(y.q || 0) - +(x.q || 0);
          }
        }

        if (isUserSort) return 0;
        const s1 = config[x.s] ? +config[x.s].displayWeight || 0 : 0;
        const s2 = config[y.s] ? +config[y.s].displayWeight || 0 : 0;
        return s2 - s1;
      });

      return newAry;

      function strCompare(a, b) {
        a = a.toLowerCase();
        b = b.toLowerCase();
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
      }
    },
    [config, tabCfg, symbolStar, isSearch]
  );

  const filterKeyword = useCallback((records, nextKeyword) => {
    return records.filter((item) => {
      const marketName = item.s;
      let str = nextKeyword.replace(/\//g, "_");
      str = str.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
      return new RegExp(str, "i").test(marketName);
    });
  }, []);

  const filterHidden = useCallback(
    (records, nextTabCfg) => {
      if (nextTabCfg.noHidden) return records;
      if (!config) return [];

      const retAry: TickerProps[] = [];
      records.map((item) => {
        const marketName = item.s;
        const theMarket = config[marketName];
        if (!isDisplayLevelAllowed(theMarket, ["FULL"])) return;
        retAry.push(item);
      });
      return retAry;
    },
    [config]
  );

  const filterResData: TickerProps[] = useMemo(() => {
    if (!config) return [];
    const fiatTabCodes = getDynamicFiatTabCodes();

    if (isSearch) {
      const records: TickerProps[] = [];

      tickers.map((item) => {
        const symbol = item.s;
        if (!isCfd && symbol.endsWith("_cfd")) return;
        if (!matchCfdSymbolMarginQuote(symbol, isFiatContract, isCfd, activeFiatQuote)) return;

        const theMarket = config[symbol];
        if (!isDisplayLevelAllowed(theMarket, ["FULL", "SEARCH"])) return;
        records.push(item);
      });

      return records;
    }

    const { buyCoinAry } = tabCfg;
    const records: TickerProps[] = [];

    tickers.map((item) => {
      const symbol = item.s;
      if (!isCfd && symbol.endsWith("_cfd")) return;
      if (!matchCfdSymbolMarginQuote(symbol, isFiatContract, isCfd, activeFiatQuote)) return;

      const quoteLower = getCfdQuoteCurrencyUpper(symbol).toLowerCase();
      if (buyCoinAry && !buyCoinAry.includes(quoteLower)) return;
      if (tabCfg.key === "user" && !symbolStar.find((obj) => obj === symbol)) return;

      const theMarket = config[symbol];
      if (!isDisplayLevelAllowed(theMarket, ["FULL", "SEARCH"])) return;
      if (tabCfg.key === "etf" && !store.market.isEtfSymbolFn(theMarket)) return;

      if (tabCfg.key === "zone") {
        const { plates } = theMarket;
        if (!plates || !plates.length) return;
        if (tabCfg.plateId && !plates.includes(tabCfg.plateId)) return;
      }

      if (tabCfg.key === "usds") {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (plates.includes(260) || plates.includes(261)) return;
      }

      const fiatTabKeys = [...fiatTabCodes, "fiat"];
      if (fiatTabKeys.includes(tabCfg.key)) {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (plates.includes(260) || plates.includes(261)) return;
      }

      if (tabCfg.key === "commodities") {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (!plates.includes(260)) return;
        if (isCfd && !isFiatContract && !isUsdtMarginQuoteCode(quoteLower)) return;
      }

      if (tabCfg.key === "stocks") {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (!plates.includes(261)) return;
        if (isCfd && !isFiatContract && !isUsdtMarginQuoteCode(quoteLower)) return;
      }

      records.push(item);
    });

    return records;
  }, [tickers, config, tabCfg, symbolStar, isSearch, isCfd, isFiatContract, activeFiatQuote]);

  const records: TickerProps[] = useMemo(() => {
    const sortAry = doSortArray(filterResData, sortBy);
    if (keyword) return filterKeyword(sortAry, keyword);
    return filterHidden(sortAry, tabCfg);
  }, [keyword, sortBy, tabCfg, filterResData, doSortArray, filterKeyword, filterHidden]);

  useEffect(() => {
    if (!config) return;
    apiReqTicker24h(() => {
      setLoading(false);
    });
  }, [config, apiReqTicker24h]);

  useEffect(() => {
    if (loading) return;
    const msg = { event: `tickers` };
    Socket.addChannel(msg, wsCallback);
    timeoutApiLoop();

    return () => {
      Socket.removeChannel(msg);
      timeoutClearApiLoop();
    };
  }, [loading, timeoutApiLoop, timeoutClearApiLoop, wsCallback]);

  useEffect(() => {
    if (tabCfg.key === "user") store.user.getSymbolStar();
  }, [tabCfg]);

  useEffect(() => {
    isLogin && store.user.getSymbolStar();
  }, [isLogin]);

  return (
    <>
      {loading && <XtLoading className={styles.loading} />}
      {!loading && !records.length && <AppDivNoData />}
      {!loading && !!records.length && (
        <XtScrollWindow className={cx(styles.main)} height={30} dataAry={records}>
          <WindowUl isShowVolume={isShowVolume} onMarketSelect={onMarketSelect} />
        </XtScrollWindow>
      )}
    </>
  );
};

export default observer(Main);
